import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompanyGig, CompanyGigShift, GigType } from "model/Gig";
import { DropdownWithLabel } from "../../general/dropDownWithLabel/dropdownWithLabel.component";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { MultiSelectOption } from "../../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { HourlyRate } from "./hourlyRate/hourlyRate.component";
import { SwitcherButton, SwitcherContainer } from "../../salary/salary.styled";
import { getShiftHours } from "utils/utils";
import { SimpleRow } from "../../organizationalStructure/organizationalStructure.styled";
import { RecruitFormData } from "./gigFormModal.component";
import { FormData } from "./gigFormModal.component";
import { GigRole } from "./gigRole.component";
import { GigSkills } from "./gigSkills.component";
import { GigPaymentType } from "./gigPaymentType.component";

type Props = {
  shifts: CompanyGigShift[];
  recruitFormData: RecruitFormData;
  setRecruitFormData: React.Dispatch<React.SetStateAction<RecruitFormData>>;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formData: FormData;
  gigData?: CompanyGig;
  havePublicModule: boolean;
  haveTeamModule: boolean;
};

export const RecruitGigForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [state] = useOrganizationalStructureStore();
  const [companyState] = useCompanyStore();

  const showTypeToggle = props.havePublicModule && props.haveTeamModule;

  useEffect(() => {
    if (props.gigData !== undefined) return;

    if (!showTypeToggle) {
      if (props.havePublicModule && props.formData.type !== GigType.PUBLIC) {
        props.setFormData({ ...props.formData, type: GigType.PUBLIC });
      } else if (
        props.haveTeamModule &&
        props.formData.type !== GigType.WORKPOOL
      ) {
        props.setFormData({ ...props.formData, type: GigType.WORKPOOL });
      }
    }
  }, [
    props.gigData,
    showTypeToggle,
    props.havePublicModule,
    props.haveTeamModule,
    props.formData,
    props.setFormData,
  ]);

  const hasSimflexityPay = useMemo(() => {
    const settings = companyState.company?.companySettings ?? [];
    return settings.some(
      (set) =>
        set.key === "SimflexityPay" && set.value?.toLowerCase() === "true"
    );
  }, [companyState.company?.companySettings]);

  const teamOptions: MultiSelectOption[] = useMemo(() => {
    return (state.structure?.entities ?? [])
      .flatMap((entity) => entity.units ?? [])
      .filter((unit) => unit.name !== "Unhandled")
      .flatMap((unit) =>
        (unit.workGroups ?? []).map((team) => ({
          value: String(team.id),
          label: `${unit.name} - ${team.name}`,
        }))
      );
  }, [state.structure]);

  const isPublic = props.formData.type === GigType.PUBLIC;

  const selectedTeamValue =
    props.recruitFormData.workGroupId != null
      ? String(props.recruitFormData.workGroupId)
      : "";

  return (
    <>
      <SimpleRow style={{ maxWidth: 500 }}>
        {showTypeToggle && (
          <SwitcherContainer style={{ marginRight: 20 }}>
            <SwitcherButton
              $active={props.formData.type === GigType.WORKPOOL}
              $disabled={props.gigData !== undefined}
              disabled={props.gigData !== undefined}
              onClick={() => {
                if (props.gigData !== undefined) return;
                props.setFormData({
                  ...props.formData,
                  type: GigType.WORKPOOL,
                });
              }}
              aria-pressed={props.formData.type === GigType.WORKPOOL}
            >
              {t("CreateShift.Team")}
            </SwitcherButton>
            <SwitcherButton
              $active={props.formData.type === GigType.PUBLIC}
              $disabled={props.gigData !== undefined}
              disabled={props.gigData !== undefined}
              onClick={() => {
                if (props.gigData !== undefined) return;
                props.setFormData({
                  ...props.formData,
                  type: GigType.PUBLIC,
                });
              }}
              aria-pressed={props.formData.type === GigType.PUBLIC}
            >
              {t("CreateShift.Public")}
            </SwitcherButton>
          </SwitcherContainer>
        )}

        <DropdownWithLabel
          label={t("CreateShift.Team")}
          options={teamOptions}
          value={selectedTeamValue}
          onChange={(val: string) =>
            props.setRecruitFormData({
              ...props.recruitFormData,
              workGroupId: val ? Number(val) : undefined,
            })
          }
          placeholder={t("CreateShift.ChooseTeam")}
          required
          inputSize="medium"
          width={200}
          disabled={props.gigData !== undefined}
        />
      </SimpleRow>

      <GigRole
        formData={props.recruitFormData}
        setFormData={props.setRecruitFormData}
      />

      {isPublic && (
        <GigSkills
          formData={props.recruitFormData}
          setFormData={props.setRecruitFormData}
        />
      )}

      {isPublic && (
        <>
          <GigPaymentType
            haveSimflexityPay={hasSimflexityPay}
            formData={props.recruitFormData}
            setFormData={props.setRecruitFormData}
          />

          <HourlyRate
            maxHourlyRate={props.recruitFormData.maxRate}
            minHourlyRate={props.recruitFormData.minRate}
            onChange={(min, max) =>
              props.setRecruitFormData({
                ...props.recruitFormData,
                minRate: min,
                maxRate: max,
              })
            }
            hours={getShiftHours(...props.shifts)}
            shifts={props.shifts}
            gigType={props.formData.type}
            paymentType={props.recruitFormData.paymentType}
          />
        </>
      )}
    </>
  );
};
